import React from "react";
import { cn } from "@/lib/utils";
import {
  SPLASH_VIEWBOX,
  SPLASH_SIZE,
  SPLASH_CENTER,
  SPLASH_MAIN,
  SPLASH_CORE,
  SPLASH_DROPS,
} from "./splashPaths";

// Each variant is the logo splash turned / mirrored / scaled, with its own set of droplets,
// so repeated splashes on a page never look copy-pasted.
export const SPLASH_VARIANTS = {
  logo: { rotate: 0, flip: false, scale: 1, drops: () => true },
  a: { rotate: -12, flip: true, scale: 1.04, drops: (i) => i % 2 === 0 },
  b: { rotate: 10, flip: false, scale: 1.08, drops: (i) => i % 3 !== 1 },
  c: { rotate: 168, flip: true, scale: 1.02, drops: (i) => i % 2 === 1 },
};

const splashTransform = ({ rotate, flip, scale }, boost = 1) => {
  const { x, y } = SPLASH_CENTER;
  const s = scale * boost;
  const sx = (flip ? -1 : 1) * s;
  return `translate(${x} ${y}) rotate(${rotate}) scale(${sx} ${s}) translate(${-x} ${-y})`;
};

/**
 * The Vromiara paint splash as inline SVG (colour = currentColor).
 * - animate: play the "splat" (body pops in, droplets fly out from the centre)
 * - play: when false the splash stays hidden until it becomes true (scroll-triggered splats)
 * - boost: extra scale on top of the variant's own scale
 * - solid: also fill the smooth inner blob (untransformed), so text placed on it is always covered
 * - mainTransform: extra SVG transform around the turned splash (e.g. squeeze it into a box)
 * - children: extra SVG content drawn above the splash, in the same coordinate space
 */
const Splash = ({
  variant = "logo",
  animate = false,
  play = true,
  delay = 0,
  showDrops = true,
  solid = false,
  boost = 1,
  mainTransform,
  preserveAspectRatio,
  className,
  style,
  children,
  ...props
}) => {
  const v = SPLASH_VARIANTS[variant] || SPLASH_VARIANTS.logo;

  return (
    <svg
      viewBox={SPLASH_VIEWBOX}
      preserveAspectRatio={preserveAspectRatio}
      className={cn(
        "splash overflow-visible",
        animate && (play ? "splash-play" : "splash-wait"),
        className
      )}
      style={{ "--splash-delay": `${delay}ms`, ...style }}
      aria-hidden={children ? undefined : true}
      focusable="false"
      {...props}
    >
      <g fill="currentColor">
        {solid && <path className="splash-body" d={SPLASH_CORE} />}
        <g transform={mainTransform}>
          <g transform={splashTransform(v, boost)}>
            <path className="splash-body" d={SPLASH_MAIN} />
            {showDrops &&
              SPLASH_DROPS.map((drop, i) =>
                v.drops(i) ? (
                  <path
                    key={i}
                    className="splash-drop"
                    d={drop.d}
                    style={{
                      "--dx": `${Math.round((SPLASH_CENTER.x - drop.cx) * 0.75)}px`,
                      "--dy": `${Math.round((SPLASH_CENTER.y - drop.cy) * 0.75)}px`,
                      "--d": `${120 + (i % 5) * 45}ms`,
                    }}
                  />
                ) : null
              )}
          </g>
        </g>
      </g>
      {children}
    </svg>
  );
};

export { SPLASH_SIZE };
export default Splash;
